import React, { FC, useEffect, useState } from "react";
import { IStyle, mergeStyles, mergeStyleSets } from "@fluentui/react";
import {
  Nomination,
  ActivityLog,
  ApiCallModel,
  LineHandling,
} from "../../models";
import { NominationDetailsPane } from "./NominationDetailsPane";
import SofIcon from "../common/Icons";
import RemarksPane from "./RemarksPane";
import NominationForm from "./NominationForm";
import { pushNotification } from "../../api/pushNotification";

type Props = {
  lineHandlingStatus: number;
  isLineHandlingRejected: boolean;
  nomination: Nomination | null;
  isEditActivityAllowed: boolean;
  isAddCommentAllowed: boolean;
  isDeleteCommentAllowed: boolean;
  isOffline: boolean;
  isApproved: boolean;
  onNominationEditStart(): void;
  onNominationEditEnd(): void;
  onNominationEdit(nom: Nomination): void;
  activityLog: ApiCallModel<ActivityLog[]> | null;
};
const buttonBase = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0",
    },
  },
} as IStyle);
const classes = mergeStyleSets({
  buttonRow: {
    border: "1px solid var(--grey-200)",
    borderBottom: 0,
    alignItems: "center",
    display: "flex",
    padding: 10,
    marginTop: "16px",
  },
  buttonRowFiller: {
    flex: "grow",
    flexGrow: 1,
  },
  button: [
    buttonBase,
    {
      backgroundColor: "var(--grey-050)",
      color: "#4C4C4C",
      display: "flex",
      alignItems: "center",
      padding: "9px 15px",
    },
  ],
  dropdown: {
    color: "var(--primary-500)",
    fontWeight: 400,
    fontSize: 16,
    marginRight: "auto",
    display: "flex"
  },
});

enum NomView {
  View = 0,
  Edit = 1,
  Comments = 2,
}

const NominationPanel: FC<Props> = (props: Props) => {
  const [lineHandlingNo, setLineHandlingNo] = useState("");
  const [nominationNo, setNominationNo] = useState("");
  const [shortNominationNo, setShortNominationNo] = useState("");
  const [view, setView] = useState<NomView>(NomView.View);
  const [isNominationEditable, setIsNominationEditable] =
    useState<boolean>(true);

  useEffect(() => {
    if (props.nomination) {
      setView(NomView.View);
      setLineHandlingNo(props.nomination.lineHandlingNo);
      setNominationNo(props.nomination.nominationNo);

      const { nominationNo } = props.nomination;
      const nomNo = nominationNo.substr(nominationNo.length - 6);
      if (nomNo) setShortNominationNo(nomNo);
    }
  }, [props.nomination]);

  useEffect(() => {
    if (props.nomination) {
      const { nominationNo } = props.nomination;

      // if (props.activityLog && props.activityLog.result) {
      //   const transferStarted = props.activityLog.result.find(
      //     f =>
      //       f.nominationNo === nominationNo &&
      //       f.description === "Transfer in progress"
      //   );

      //   if (transferStarted) {
      //     setIsNominationEditable(false);
      //   }
      // }
      if (props.lineHandlingStatus === 4) {
        setIsNominationEditable(false);
      }
    }
  });

  useEffect(() => {
    if (view === NomView.Edit) {
      props.onNominationEditStart();
    }
  }, [view]);

  useEffect(() => {
    pushNotification.connection.on("NominationUpdated", (message) => {
      var result = message as Nomination;
      if (
        props.nomination &&
        result.nominationNo === props.nomination.nominationNo
      ) {
        setView(NomView.View);
        props.onNominationEdit(result);
        props.onNominationEditEnd();
      }
    });
  }, [props.nomination]);

  return (
    <>
      {/* <div>
        <dt>Nomination</dt>
      </div> */}
      <div className={classes.buttonRow}>
        <div id="btn-nomination-no" className={classes.dropdown}>
          Nomination: <b>{shortNominationNo}</b>
        </div>
        {/* <span className={classes.buttonRowFiller} /> */}
        {view === NomView.View && (
          <div className="d-flex w-100" style={{ justifyContent: "end" }}>
            <button
              id="btn-nomination-comment"
              className={classes.button}
              onClick={() => setView(NomView.Comments)}
            >
              <SofIcon iconname="Comment" />
              {/* &nbsp; Comments */}
            </button>
            {props.nomination &&
              props.isEditActivityAllowed &&
              isNominationEditable &&
              !props.isLineHandlingRejected && (
                <>
                  &nbsp;
                  <button
                    id="btn-edit-nomination"
                    className={classes.button}
                    onClick={() => setView(NomView.Edit)}
                  >
                    <SofIcon iconname="Edit" />
                    {/* &nbsp; Edit */}
                  </button>
                </>
              )}
          </div>
        )}

        {view === NomView.Comments && (
          <button
            className={classes.button}
            onClick={() => setView(NomView.View)}
          >
            <SofIcon iconname="Back" />
            &nbsp; Back
          </button>
        )}
      </div>
      {view === NomView.Comments && (
        <RemarksPane
          lineHandlingNo={lineHandlingNo}
          nominationNo={nominationNo}
          isAddCommentAllowed={props.isAddCommentAllowed}
          isDeleteCommentAllowed={props.isDeleteCommentAllowed}
          isOffline={props.isOffline}
          readonly={props.isApproved}
          onClose={() => setView(NomView.View)}
        />
      )}
      {view === NomView.View && props.nomination && (
        <NominationDetailsPane nomination={props.nomination} />
      )}

      {view === NomView.Edit && props.nomination && (
        <NominationForm
          {...props.nomination}
          lineHandlingStatus={props.lineHandlingStatus}
          onSuccess={(result: Nomination) => {
            setView(NomView.View);
            props.onNominationEdit(result);
            props.onNominationEditEnd();
          }}
          onClose={() => {
            setView(NomView.View);
            props.onNominationEditEnd();
          }}
        />
      )}
    </>
  );
};

export default NominationPanel;
